<script lang="ts">
  import type { FliteImage, FliteVideo } from "$lib/gql/genCms/codegen";
  import { Position, MediaType } from "$lib/gql/genCms/codegen";
  import type { CardConnection } from "$lib/gql/genCms/codegen";
  import Column from "$lib/components/Column/Column.svelte";
  import { shopTheLookImgConfig } from "../ShowcaseFeature/ShowcaseFeature.image-config";
  import CardWrapper from "$lib/components/Cards/CardWrapper/CardWrapper.svelte";
  import ShowcaseFeature from "../ShowcaseFeature/ShowcaseFeature.svelte";
  import { onMount } from "svelte";

  export let mediaType: MediaType = MediaType.Image;
  export let isFirstBlock: boolean = false;
  export let image: FliteImage = {};
  export let imageSm: FliteImage = {};
  export let imageAlt: string = "";
  export let title: string;
  export let body: string;
  export let ctaOneText: string;
  export let ctaOneLink: string;
  export let ctaTwoText: string;
  export let ctaTwoLink: string;
  export let ctaOneBgColour: string;
  export let ctaTwoBgColour: string;
  export let ctaOneTextLight = false;
  export let ctaTwoTextLight = false;
  export let invert = false;
  export let headingElement = "h1";
  export let video: FliteVideo = {};
  export let videoSm: FliteVideo = {};
  export let cards: CardConnection = null;
  export let url: string;
  export let blockCtaText: string = "";
  export let blockCtaLink: string = "";
  export let rectangleDivider = false;

  let viewportWidth: number;
  let isMobile = false;
  let mdWidth = 768;

  onMount(() => {
    const bodyStyle = getComputedStyle(document.body);
    mdWidth = Number(bodyStyle.getPropertyValue("--md").replace(/\D/g, ""));
  });

  $: isMobile = viewportWidth < mdWidth;
</script>

<style lang="scss">
  .shop-the-look {
    contain: paint;
    border-bottom: 1px solid $background;

    @include retina {
      border-bottom: 0.5px solid $background;
    }

    :global(.content-wrapper--absolute) {
      position: relative;
      top: auto;
      left: auto;
      right: auto;
      bottom: auto;
      z-index: 50;
    }

    :global(.media-container) {
      @include breakpoint(md) {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 115vw;
      }

      @include breakpoint(lg) {
        height: 81vw;
      }

      @include breakpoint(xl) {
        height: 73.6vw;
      }

      @include breakpoint(xxl) {
        height: 73.8vw;
      }
    }

    :global(.media-container > div) {
      @include breakpoint(md) {
        position: sticky;
        top: 0;
      }
    }

    :global(.wrapper) {
      @include breakpoint(md) {
        max-height: 87.4vw;
      }

      @include breakpoint(lg) {
        max-height: 47.3vw;
      }

      @include breakpoint(xl) {
        max-height: 47.45vw;
      }

      @include breakpoint(xxl) {
        max-height: 47.65vw;
      }
    }
  }

  .shop-the-look.singleCard {
    :global(.media-container) {
      @include breakpoint(md) {
        height: 100.1vw;
      }
      @include breakpoint(lg) {
        height: 65vw;
      }
      @include breakpoint(xl) {
        height: 63.75vw;
      }
      @include breakpoint(xxl) {
        height: 68vw;
      }
    }
  }

  .card-wrapper {
    position: relative;
    margin: -4rem 1rem 1rem 1rem;

    @include breakpoint(md) {
      margin: 220px -24px 20px -24px;
    }

    @include breakpoint(lg) {
      margin: 150px 0 48px;
    }

    @include breakpoint(xl) {
      margin: 188px 0 64px;
    }
  }

  :global(.shop-the-look .card-wrapper.desktop) {
    display: none;

    @include breakpoint(md) {
      display: block;
    }
  }

  :global(.shop-the-look .card-wrapper.mobile) {
    display: block;

    @include breakpoint(md) {
      display: none;
    }
  }

  :global(.shop-the-look .wrapper-inner) {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    @include breakpoint(md) {
      align-items: start;
      text-align: start;
    }
  }

  :global(.shop-the-look .ctas) {
    display: flex;
    justify-content: center !important;
    @include breakpoint(md) {
      justify-content: flex-start;
    }
  }
</style>

<div
  class="shop-the-look"
  data-cs-override-id={cards.edges
    ? `shop-the-look-${cards.edges.length}-grid`
    : "shop-the-look"}
  class:singleCard={cards.edges && cards.edges.length === 1}
>
  <ShowcaseFeature
    fluid={true}
    {mediaType}
    {image}
    {imageSm}
    {imageAlt}
    {title}
    subtitle=""
    {body}
    {ctaOneLink}
    {ctaOneText}
    {ctaTwoLink}
    {ctaTwoText}
    {invert}
    {headingElement}
    {videoSm}
    {video}
    position={Position.Left}
    imageConfig={shopTheLookImgConfig}
    mediaColumns={10}
    displayDirection={isMobile ? "column-reverse" : "absolute"}
    {blockCtaText}
    {blockCtaLink}
    {isFirstBlock}
    useAltFont={true}
    headingVariant="h1"
    borderBottom={false}
    {rectangleDivider}
    {ctaOneBgColour}
    {ctaTwoBgColour}
    {ctaOneTextLight}
    {ctaTwoTextLight}
  >
    <svelte:fragment slot="cards">
      {#if cards && cards.edges[0] !== null && cards.edges.length >= 1}
        <Column
          smSpan={4}
          mdSpan={4}
          mdStart={5}
          lgStart={8}
          lgSpan={5}
          xlSpan={5}
          xxlSpan={8}
        >
          <div class="card-wrapper desktop">
            <CardWrapper {cards} {url} />
          </div>
        </Column>
      {/if}
    </svelte:fragment>
  </ShowcaseFeature>

  {#if cards && cards.edges[0] !== null && cards.edges.length >= 1}
    <Column
      smSpan={4}
      mdSpan={4}
      mdStart={5}
      lgStart={8}
      lgSpan={5}
      xlSpan={5}
      xxlSpan={8}
    >
      <div class="card-wrapper mobile">
        <CardWrapper {cards} {url} />
      </div>
    </Column>
  {/if}
</div>

<svelte:window bind:innerWidth={viewportWidth} />
